import { useLocation } from 'react-router-dom'
import { useMemo, useCallback, useState } from 'react'
import { toast } from 'sonner'

import { Skeleton } from '@/components/ui/skeleton'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import { useDutySummary } from '@/hooks/useDutySummary'
import {
  getEvalTestCasesQueryParams,
  mapEvalTestCaseToListItem,
} from '../evalCasePresentation'
import type { EvalRunLifecycleStatus } from '../types'
import { useStartEvalCaseRunsMutation } from '../hooks/useStartEvalCaseRunMutation'
import { useEvalTestCasesQuery } from '../hooks/useEvalTestCasesQuery'
import { useRelayEvalRunsQuery } from '../hooks/useRelayEvalRunsQuery'
import { CreateChatCaseModal } from './CreateChatCaseModal'
import { EvalCaseList } from './EvalCaseList'
import { EvalDetailStatePanel } from './EvalDetailStatePanel'
import { EvalLibraryHeader } from './EvalLibraryHeader'

const MAX_RUN_ALL_CASE_IDS = 100

interface EvalDutyDetailProps {
  dutyId: string
}

export function EvalDutyDetail({ dutyId }: EvalDutyDetailProps) {
  const { toTask } = useAppNavigate()
  const location = useLocation()
  const testCasesQueryParams = useMemo(
    () => getEvalTestCasesQueryParams(dutyId),
    [dutyId]
  )
  const testCasesQuery = useEvalTestCasesQuery(testCasesQueryParams)
  const startEvalCaseRunsMutation = useStartEvalCaseRunsMutation()
  const apiTestCases = useMemo(
    () => testCasesQuery.data?.pages.flatMap((page) => page.data) ?? [],
    [testCasesQuery.data]
  )
  const cases = useMemo(
    () => apiTestCases.map(mapEvalTestCaseToListItem),
    [apiTestCases]
  )
  const caseNameById = useMemo(
    () => new Map(cases.map((evalCase) => [evalCase.id, evalCase.name])),
    [cases]
  )
  const allCaseIds = useMemo(() => cases.map((c) => c.id), [cases])
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [queuedCaseIds, setQueuedCaseIds] = useState<Set<string>>(
    () => new Set()
  )
  const [activeRunIds, setActiveRunIds] = useState<string[]>([])
  const [latestRunIdByCaseId, setLatestRunIdByCaseId] = useState<
    Map<string, string>
  >(() => new Map())
  const activeRunsQuery = useRelayEvalRunsQuery(activeRunIds)
  // Key statuses by each case's latest run id (not case_id) so a rerun shows
  // the queued fallback until its new run appears, instead of the previous
  // run's stale terminal status lingering via keepPreviousData.
  const runStatusByCaseId = useMemo(() => {
    const statusByRunId = new Map(
      (activeRunsQuery.data?.items ?? []).map((run) => [run.id, run.status])
    )
    const statuses = new Map<string, EvalRunLifecycleStatus>()
    for (const [caseId, runId] of latestRunIdByCaseId) {
      const status = statusByRunId.get(runId)
      if (status) statuses.set(caseId, status)
    }
    return statuses
  }, [activeRunsQuery.data, latestRunIdByCaseId])

  const navState = useMemo(() => {
    const s = location.state as { dutyName?: string } | null
    return s ?? {}
  }, [location.state])

  const { data: summary } = useDutySummary(dutyId, {
    name: navState.dutyName,
  })

  const dutyName =
    summary?.name ?? navState.dutyName ?? `Duty ${dutyId.slice(0, 8)}`

  const handleOpenCase = useCallback(
    (caseId: string) => {
      const evalCase = cases.find((item) => item.id === caseId)
      const priorState =
        location.state && typeof location.state === 'object'
          ? location.state
          : {}

      toTask(`/evals/${dutyId}/test-cases/${caseId}`, {
        replace: false,
        state: {
          ...priorState,
          caseName: evalCase?.name,
        },
      })
    },
    [toTask, dutyId, location.state, cases]
  )

  const handleRunCase = useCallback(
    async (caseId: string) => {
      const caseName = caseNameById.get(caseId) ?? 'test case'

      setQueuedCaseIds((current) => {
        const next = new Set(current)
        next.add(caseId)
        return next
      })
      const toastId = toast.loading(`Queueing ${caseName}...`)

      try {
        const result = await startEvalCaseRunsMutation.mutateAsync([caseId])
        const run = result.items.find((item) => item.case_id === caseId)
        if (!run) throw new Error(`Relay did not return a run for ${caseName}.`)

        setActiveRunIds((current) => Array.from(new Set([...current, run.id])))
        setLatestRunIdByCaseId((current) =>
          new Map(current).set(caseId, run.id)
        )

        toast.success(`Queued ${caseName}.`, { id: toastId })
      } catch (error) {
        setQueuedCaseIds((current) => {
          const next = new Set(current)
          next.delete(caseId)
          return next
        })
        toast.error(
          error instanceof Error
            ? error.message
            : `Unable to queue ${caseName}.`,
          { id: toastId }
        )
      }
    },
    [caseNameById, startEvalCaseRunsMutation]
  )

  const handleRunAll = useCallback(async () => {
    if (allCaseIds.length === 0) {
      toast.info('No test cases available to run.')
      return
    }

    if (allCaseIds.length > MAX_RUN_ALL_CASE_IDS) {
      toast.error(
        `Run all supports up to ${MAX_RUN_ALL_CASE_IDS} test cases at a time.`
      )
      return
    }

    setQueuedCaseIds(new Set(allCaseIds))
    const caseLabel = allCaseIds.length === 1 ? 'case' : 'cases'
    const toastId = toast.loading(
      `Queueing ${allCaseIds.length} test ${caseLabel}...`
    )

    try {
      const result = await startEvalCaseRunsMutation.mutateAsync(allCaseIds)
      const runIds = result.items.map((run) => run.id)
      const queuedIds = result.items.map((run) => run.case_id)
      setActiveRunIds(runIds)
      setLatestRunIdByCaseId(
        new Map(result.items.map((run) => [run.case_id, run.id]))
      )
      if (queuedIds.length > 0) {
        setQueuedCaseIds(new Set(queuedIds))
      }
      toast.success(`Queued ${allCaseIds.length} test ${caseLabel}.`, {
        id: toastId,
      })
    } catch (error) {
      setQueuedCaseIds(new Set())
      setActiveRunIds([])
      setLatestRunIdByCaseId(new Map())
      toast.error(
        error instanceof Error ? error.message : 'Unable to queue test cases.',
        { id: toastId }
      )
    }
  }, [allCaseIds, startEvalCaseRunsMutation])

  const handleOpenCreate = useCallback(() => setIsCreateOpen(true), [])
  const handleCloseCreate = useCallback(() => setIsCreateOpen(false), [])

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto px-5 py-6 lg:px-6">
      <div className="flex min-h-0 w-full flex-1 flex-col gap-5">
        <EvalLibraryHeader
          dutyName={dutyName}
          onRunAll={handleRunAll}
          onRefresh={testCasesQuery.refetch}
          isRefreshing={testCasesQuery.isFetching}
          onNewCase={handleOpenCreate}
        />

        <section className="w-full flex-col pt-0">
          {testCasesQuery.isLoading ? (
            <div className="flex w-full flex-col gap-3" role="status">
              <span className="sr-only">Loading test cases...</span>
              {[0, 1, 2, 3].map((key) => (
                <Skeleton key={key} className="h-20 w-full rounded-2xl" />
              ))}
            </div>
          ) : null}

          {testCasesQuery.isError ? (
            <EvalDetailStatePanel
              state="error"
              label="Unable to load test cases."
            />
          ) : null}

          {!testCasesQuery.isLoading &&
          !testCasesQuery.isError &&
          cases.length === 0 ? (
            <EvalDetailStatePanel state="empty" label="No test cases found." />
          ) : null}

          {!testCasesQuery.isLoading &&
          !testCasesQuery.isError &&
          cases.length > 0 ? (
            <EvalCaseList
              cases={cases}
              queuedCaseIds={queuedCaseIds}
              runStatusByCaseId={runStatusByCaseId}
              onOpenCase={handleOpenCase}
              onRunCase={handleRunCase}
            />
          ) : null}
        </section>
      </div>

      {isCreateOpen ? (
        <CreateChatCaseModal dutyId={dutyId} onClose={handleCloseCreate} />
      ) : null}
    </div>
  )
}
